import type { FC } from "react"
import { useCallback } from "react"

import { useCancelInvoice } from "../hooks/useCancelInvoice"

import { InvoiceCommentConfirmDialog } from "./InvoiceCommentConfirmDialog"

import { EntityLockBanner, EntityLockConflictDialog, useEntityLock, useEntityLockConflictDialog } from "@/entityLock"
import { MutationLockResultStatus } from "@/entityLock/types"
import type { Invoice } from "@/graphql/codegen/graphql"
import { EntityLockEntityType } from "@/graphql/codegen/graphql"

interface InvoiceCancelDialogProps {
  invoice: Invoice
  onClose: () => void
  onSuccess: () => void
  onRefresh?: () => void
}

export const InvoiceCancelDialog: FC<InvoiceCancelDialogProps> = ({ invoice, onClose, onSuccess, onRefresh }) => {
  const { cancelInvoice, isCancelling } = useCancelInvoice()

  const { holder, isReadOnly, markLost } = useEntityLock({
    entityType: EntityLockEntityType.Invoice,
    entityId: invoice.invoiceId,
    enabled: true,
  })

  const { isLockConflictOpen, lockConflictMessage, openLockConflict, closeLockConflict, handleLockConflictRefresh } =
    useEntityLockConflictDialog({ onRefresh, markLost })

  const handleConfirm = useCallback(
    async (comment: string) => {
      const result = await cancelInvoice({ invoiceId: invoice.invoiceId, comment })
      if (result.status === MutationLockResultStatus.Success) {
        onSuccess()
        return
      }
      if (result.status === MutationLockResultStatus.LockConflict) {
        openLockConflict(result.message)
      }
    },
    [cancelInvoice, invoice.invoiceId, openLockConflict, onSuccess]
  )

  if (isLockConflictOpen && lockConflictMessage) {
    return (
      <EntityLockConflictDialog
        message={lockConflictMessage}
        onClose={closeLockConflict}
        onRefresh={handleLockConflictRefresh}
      />
    )
  }

  return (
    <InvoiceCommentConfirmDialog
      title='Are you sure you want to cancel this invoice?'
      content={`Invoice ${invoice.invoiceNumber} will be moved to Cancelled status.`}
      commentLabel='Reason for cancelling'
      commentPlaceholder='Please explain why this invoice is being cancelled'
      confirmLabel='Yes, Cancel Invoice'
      closeLabel='Go Back'
      warning
      isProcessing={isCancelling}
      isSubmitDisabled={isReadOnly}
      banner={isReadOnly ? <EntityLockBanner holder={holder} /> : undefined}
      onClose={onClose}
      onConfirm={handleConfirm}
      testIdPrefix='invoices.cancel-dialog'
    />
  )
}
